import dayjs from 'dayjs';

import { LineItemsByCampaign } from '../../apis/graphql/campaigns/useFetchLineItemsByCampaignId';
import {
  CampaignEdge,
  CreativeStatus,
  LineItemStatus,
  LineItemTargets,
  LineItemTargetValue,
  Maybe,
  // PublisherType,
  TargetData,
  UserOrganizationInfoRoles,
} from '../../apis/graphql/types';
import { ROLES } from '../../components/User/constants';
import {
  CANCELLABLE_STATUSES,
  DELETABLE_STATUS,
  DOWNLOADABLE_STATUSES,
  EDITABLE_STATUSES,
  LINE_ITEMS_STATUSES,
  PAUSABLE_STATUSES,
  RESUMABLE_STATUSES,
  SUSPENDABLE_STATUSES,
} from './constants';
import {
  ADS_SECTION_IDS,
  AGES,
  AUDIENCES,
  CONTENT_TARGETINGS,
  DELIVERY_SECTION_IDS,
  LOCATION_TARGETINGS,
  PLATFORM,
  RETARGETINGS,
  SETUP_SECTION_IDS,
  TARGETING_SECTION_IDS,
} from './Form/constants';
import { LineItem, TargetingType } from './Form/types';
import { REPORT_ROUTES } from './Summary/CampaignSummary/CampaignReports/constants';

type Props = {
  statusList: (string | null | undefined)[];
  isModificationAllowed?: boolean;
  isCampaign?: boolean;
  isMarketerOrg?: boolean;
  isAdminTool?: boolean;
};

type IsSuspended = {
  isAdminTool: boolean;
  isUserHasPermissions: boolean;
  statusList: (string | null | undefined)[];
};

export const isEditable = ({ isModificationAllowed, isCampaign, statusList }: Props): boolean => {
  return (isModificationAllowed &&
    (isCampaign ? true : EDITABLE_STATUSES.some((status) => statusList.includes(status)))) as boolean;
};

export const isPausable = ({ isModificationAllowed, statusList }: Props): boolean => {
  return (isModificationAllowed && PAUSABLE_STATUSES.some((status) => statusList.includes(status))) as boolean;
};

export const isSuspended = ({ isUserHasPermissions, statusList, isAdminTool }: IsSuspended): boolean => {
  const isIncludeSuspendedEntities = SUSPENDABLE_STATUSES.some((status) => statusList.includes(status));

  return isAdminTool && isUserHasPermissions && isIncludeSuspendedEntities;
};

export const isCancellable = ({ isModificationAllowed, statusList, isAdminTool = false }: Props): boolean => {
  if (isAdminTool) {
    return [...CANCELLABLE_STATUSES, LineItemStatus.Suspended].some((status) => statusList.includes(status));
  }

  return (isModificationAllowed && CANCELLABLE_STATUSES.some((status) => statusList.includes(status))) as boolean;
};

export const isResumable = ({ isModificationAllowed, statusList, isAdminTool = false }: Props): boolean => {
  if (isAdminTool) {
    return statusList.includes(LineItemStatus.Suspended);
  }

  return (isModificationAllowed && RESUMABLE_STATUSES.some((status) => statusList.includes(status))) as boolean;
};

export const isDeletable = ({ isModificationAllowed, statusList }: Props): boolean => {
  return (isModificationAllowed && statusList.every((status) => status === DELETABLE_STATUS)) as boolean;
};

export const isDownloadable = ({ statusList }: Props): boolean => {
  return DOWNLOADABLE_STATUSES.some((status) => statusList.includes(status));
};

export const isDisabledDelete = (lineItems: LineItem[], userId: string): boolean => {
  const isLastUpdateLessFiveMinute = lineItems?.some(({ updatedAt }): boolean => {
    if (updatedAt) {
      const updatedAtDate = new Date(updatedAt);
      const currentDate = new Date();

      const diffMilliseconds = +currentDate - +updatedAtDate;
      const fiveMinutesInMilliseconds = 5 * 60 * 1000;

      return diffMilliseconds <= fiveMinutesInMilliseconds;
    }

    return false;
  });

  const isLineItemUpdateByAnotherUser = lineItems?.some(({ updatedBy }): boolean => updatedBy !== userId);

  return !!isLastUpdateLessFiveMinute && !!isLineItemUpdateByAnotherUser;
};

export const isModificationAllowed = (roles?: Array<UserOrganizationInfoRoles>): boolean => {
  return roles?.some(
    (item) => item.role !== ROLES.manager && item.role !== ROLES.member && item.role !== ROLES.analyst
  ) as boolean;
};

export const isEnabledToResume = (adStatusList?: (string | null | undefined)[]): boolean =>
  !!adStatusList?.includes(CreativeStatus.Published) || !!adStatusList?.includes(CreativeStatus.Vast);

export const formatDate = (date?: Date | null, format = 'YYYY-MM-DD'): string => dayjs(date).format(format);

export const getTargetsArray = (values: { [key: string]: TargetingType[] }): TargetData[] => {
  const targets: TargetData[] = [];

  [CONTENT_TARGETINGS, RETARGETINGS, AUDIENCES, LOCATION_TARGETINGS, AGES, PLATFORM].forEach((fieldName) => {
    values[fieldName]?.forEach(({ id, isExclusion = false }) => {
      targets.push({
        targetId: id,
        isExclusion,
      });
    });
  });

  return targets;
};

export const getFieldElements = (): {
  setupElements: HTMLElement[];
  targetingElements: HTMLElement[];
  deliveryElements: HTMLElement[];
  adElements: HTMLElement[];
} => {
  const setupElements: HTMLElement[] = [];
  const targetingElements: HTMLElement[] = [];
  const deliveryElements: HTMLElement[] = [];
  const adElements: HTMLElement[] = [];

  const { setup, name, dateRange, budget } = SETUP_SECTION_IDS;
  const { targeting, location, gender, audiences, ageGroups, retargeting, platform, contentTargeting } =
    TARGETING_SECTION_IDS;
  const { delivery, daypart, pacing, frequency } = DELIVERY_SECTION_IDS;
  const { ads, adUpload } = ADS_SECTION_IDS;

  [setup, name, dateRange, budget].forEach((element) => {
    setupElements.push(document.getElementById(element) as HTMLElement);
  });

  [targeting, location, gender, ageGroups, audiences, retargeting, platform, contentTargeting].forEach((element) => {
    targetingElements.push(document.getElementById(element) as HTMLElement);
  });

  [delivery, daypart, pacing, frequency].forEach((element) => {
    deliveryElements.push(document.getElementById(element) as HTMLElement);
  });

  [ads, adUpload].forEach((element) => {
    adElements.push(document.getElementById(element) as HTMLElement);
  });

  return {
    setupElements,
    targetingElements,
    deliveryElements,
    adElements,
  };
};

export const getItemTypeName = (isCampaign: boolean): string => (isCampaign ? 'Campaign' : 'Line Item');
export const getPluralEnding = (isCampaign: boolean): string => (isCampaign ? 's' : '');

export const getDate = (abbreviateDate: string): Date => {
  const dateArray = abbreviateDate.split('/');
  return new Date(Number(dateArray[2]), Number(dateArray[0]) - 1, Number(dateArray[1]));
};

export const getLineItemsMinAndMaxDate = <T extends { startTime: string; endTime: string }>(
  lineItems: T[]
): { minDate: Date; maxDate: Date } => {
  let minDate = new Date();
  let maxDate = new Date();

  lineItems?.forEach((item) => {
    if (!!item.startTime) {
      const currentStartDate = getDate(item.startTime);
      if (currentStartDate.getTime() < minDate.getTime()) minDate = currentStartDate;
    }

    if (!!item.endTime) {
      const currentEndDate = getDate(item.endTime);
      if (currentEndDate.getTime() > maxDate.getTime()) maxDate = currentEndDate;
    }
  });

  return { minDate, maxDate };
};

export const isAddAccountHasOnlyDraftLineItems = (campaigns: CampaignEdge[]): boolean => {
  const lineItems = campaigns?.flatMap((campaign) => campaign.node.lineItems);

  if (!lineItems?.length) return true;

  return !lineItems?.some((lineItem) => lineItem?.status !== LINE_ITEMS_STATUSES.draft);
};

export const hasAllLineItemsInDraftStatus = (lineItems: LineItemsByCampaign): boolean =>
  !lineItems?.some(
    (lineItem) => lineItem.status !== LINE_ITEMS_STATUSES.draft && Number(lineItem.deliveredImpressions) > 0
  );

export const isMutateButtonDisabled = (
  lastPathIndex: string,
  isCampaignInDraftStatus: boolean,
  isAddAccountHasOnlyDraftLineItems: boolean,
  campaignId?: string
): boolean => {
  const isMutateReportRoute: boolean =
    lastPathIndex === REPORT_ROUTES.create.slice(1) || lastPathIndex === REPORT_ROUTES.edit.slice(1);

  if (!campaignId) return isMutateReportRoute || isAddAccountHasOnlyDraftLineItems;

  return isMutateReportRoute || Boolean(campaignId && isCampaignInDraftStatus);
};

export const hasUnallowedTargets = (targets: Maybe<LineItemTargets>, status: String = ''): boolean => {
  if (
    !targets ||
    status === LineItemStatus.Cancelled ||
    status === LineItemStatus.Complete ||
    status === LineItemStatus.Suspended
  ) {
    return false;
  }
  return Object.values(targets as LineItemTargets)
    .filter((maybeArray) => maybeArray instanceof Array)
    .flatMap((maybeTargetValue) => maybeTargetValue as Maybe<LineItemTargetValue>)
    .filter((maybeTargetValue) => !!maybeTargetValue?.hasOwnProperty('isAllowed'))
    .map((targetValue) => targetValue as LineItemTargetValue)
    .reduce((accumulator: boolean, current: Maybe<LineItemTargetValue>) => accumulator || !current?.isAllowed, false);
};

export const isAdRejected = (adStatus: CreativeStatus | null): boolean => adStatus === CreativeStatus.Rejected;

export const isLineItemInEditableStatus = (status: string): boolean => {
  const editableStatuses = [LineItemStatus.Draft, LineItemStatus.Ready, LineItemStatus.Live, LineItemStatus.Paused];

  return editableStatuses.some((editableStatus) => editableStatus === status);
};

export const isAllLineItemsCancelledOrCompleted = (lineItemsStatus: string[]): boolean => {
  return lineItemsStatus.every(
    (status: string) => status === LineItemStatus.Cancelled || status === LineItemStatus.Complete
  );
};
